import React, { useState, useEffect } from "react";
import { Card, Input, Button, Select, Table, Tag, Space, Tooltip } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PoweroffOutlined,
} from "@ant-design/icons";
import Modal from "../../components/Modal";
import { useGet, usePost, useDynamicApi } from "../../hooks/useApi";
import { useDebounce } from "../../hooks/useDebounce";
import { API_ENDPOINTS } from "../../config/api";
import Toast from "../../utils/toast";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const { Option } = Select;

const ThemeManager = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [themeToDelete, setThemeToDelete] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    is_active: true,
  });

  // Form validation errors
  const [formErrors, setFormErrors] = useState({});

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const debouncedStatusFilter = useDebounce(statusFilter, 500);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Data states
  const [themeList, setThemeList] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const STATUS_OPTIONS = [
    { value: "active", label: t("common.active") },
    { value: "inactive", label: t("common.inactive") },
  ];

  // API hooks - Fixed filter params
  const { execute: fetchList, loading: loadingList } = useGet(
    `${API_ENDPOINTS.THEMES_LIST}?page=${currentPage}&page_size=${pageSize}&search=${debouncedSearchTerm}&status=${statusFilter || ""}`,
  );

  const { post, loading: creating } = usePost(API_ENDPOINTS.THEMES_LIST);
  const { execute: getById, loading: loadingById } = useDynamicApi("GET");
  const { execute: putData, loading: updating } = useDynamicApi("PUT");
  const { execute: patchData, loading: updatingStatus } =
    useDynamicApi("PATCH");
  const { execute: deleteData, loading: deleting } = useDynamicApi("DELETE");

  // Loading state
  const loading =
    loadingList ||
    loadingById ||
    updating ||
    updatingStatus ||
    deleting ||
    creating ||
    submitting;

  // Load themes data
  const loadData = async () => {
    try {
      const response = await fetchList();
      if (response) {
        setThemeList(response.data || []);
        setTotalPages(response.pagination?.total_pages || 1);
        setTotalCount(response.pagination?.total_count || 0);
      }
    } catch (error) {
      console.error("Error loading themes:", error);
    }
  };

  useEffect(() => {
    loadData();
  }, [currentPage, pageSize]);

  // Fixed: Use statusFilter directly instead of debouncedStatusFilter
  useEffect(() => {
    setCurrentPage(1);
    loadData();
  }, [debouncedSearchTerm, statusFilter]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Reset form data
  const resetForm = () => {
    setFormData({
      name: "",
      is_active: true,
    });
    setFormErrors({});
    setSelectedTheme(null);
    setIsEditMode(false);
    setIsViewMode(false);
  };

  // Reset filters
  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
  };

  // Validate form data
  const validateForm = () => {
    const errors = {};

    if (!formData.name || formData.name.trim() === "") {
      errors.name = t("themeManager.nameRequired");
    } else if (formData.name.trim().length < 2) {
      errors.name = t("themeManager.nameMinLength");
    } else if (formData.name.trim().length > 200) {
      errors.name = t("themeManager.nameMaxLength");
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle create
  const handleCreate = async (e) => {
    e?.preventDefault?.();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const response = await post(formData);

      if (response?.status === true) {
        Toast.success(response?.message || t("themeManager.createdSuccess"));
        await loadData();
        setIsModalOpen(false);
        resetForm();
      } else {
        if (response?.errors) {
          setFormErrors(response.errors);
        } else {
          Toast.error(response?.message || t("themeManager.failedCreate"));
        }
      }
    } catch (error) {
      console.error("Error creating theme:", error);

      if (error?.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else if (error?.response?.data?.message) {
        Toast.error(error.response.data.message);
      } else {
        Toast.error(error.message || t("themeManager.failedCreate"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle update
  const handleUpdate = async (e) => {
    e?.preventDefault?.();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const endpoint = `${API_ENDPOINTS.THEMES_UPDATE}/${selectedTheme.id}`;
      const response = await putData(endpoint, formData);

      if (response?.status === true) {
        Toast.success(response?.message || t("themeManager.updatedSuccess"));
        await loadData();
        setIsModalOpen(false);
        resetForm();
      } else {
        if (response?.errors) {
          setFormErrors(response.errors);
        } else {
          Toast.error(response?.message || t("themeManager.failedUpdate"));
        }
      }
    } catch (error) {
      console.error("Error updating theme:", error);

      if (error?.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else if (error?.response?.data?.message) {
        Toast.error(error.response.data.message);
      } else {
        Toast.error(error.message || t("themeManager.failedUpdate"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle toggle active status
  const handleToggleActive = async (item) => {
    try {
      const newStatus = !item.is_active;
      const endpoint = `${API_ENDPOINTS.THEMES_UPDATE}/${item.id}`;

      const response = await patchData(endpoint, {
        is_active: newStatus,
      });

      if (response?.status === true) {
        Toast.success(
          response?.message ||
            (newStatus ? t("common.active") : t("common.inactive")),
        );
        await loadData();
      } else {
        Toast.error(response?.message || t("common.failedUpdateStatus"));
      }
    } catch (error) {
      console.error("Error updating status:", error);
      Toast.error(error.message || t("common.failedUpdateStatus"));
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (item) => {
    setThemeToDelete(item);
    setIsDeleteModalOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!themeToDelete) return;

    try {
      const endpoint = `${API_ENDPOINTS.THEMES_UPDATE}/${themeToDelete.id}`;
      const response = await deleteData(endpoint);

      if (response?.status === true) {
        Toast.success(response?.message || t("themeManager.deletedSuccess"));
        await loadData();
      } else {
        Toast.error(response?.message || t("themeManager.failedDelete"));
      }
    } catch (error) {
      console.error("Error deleting theme:", error);
      Toast.error(error.message || t("themeManager.failedDelete"));
    } finally {
      setIsDeleteModalOpen(false);
      setThemeToDelete(null);
    }
  };

  // Open modal for create
  const openCreateModal = () => {
    resetForm();
    setIsEditMode(false);
    setIsViewMode(false);
    setIsModalOpen(true);
  };

  // Open modal for edit
  const openEditModal = async (item) => {
    try {
      const endpoint = `${API_ENDPOINTS.THEMES_UPDATE}/${item.id}`;
      const response = await getById(endpoint);

      if (response?.status === true && response.data) {
        setSelectedTheme(response.data);
        setFormData({
          name: response.data.name || "",
          is_active: response.data.is_active,
        });
        setIsEditMode(true);
        setIsViewMode(false);
        setIsModalOpen(true);
      } else {
        Toast.error(t("themeManager.failedFetchDetails"));
      }
    } catch (error) {
      console.error("Error fetching theme details:", error);
      Toast.error(error.message || t("themeManager.failedFetchDetails"));
    }
  };

  // Navigate to view page
  const openViewPage = (id) => {
    navigate(`/themes/details/${id}`);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const hasFilters = searchTerm || statusFilter;

  const columns = [
    {
      title: t("themeManager.name"),
      dataIndex: "name",
      key: "name",
      render: (text) => text || "N/A",
    },
    {
      title: t("common.createdAt"),
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => formatDate(date),
    },
    {
      title: t("common.status"),
      dataIndex: "is_active",
      key: "status",
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? "green" : "default"}>
          {isActive ? t("common.active") : t("common.inactive")}
        </Tag>
      ),
    },
    {
      title: t("common.actions"),
      key: "actions",
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip
            title={record.is_active ? t("common.inactive") : t("common.active")}
          >
            <Button
              className="btn-toggle-active"
              size="small"
              danger={!record.is_active}
              type={record.is_active ? "default" : "primary"}
              icon={<PoweroffOutlined />}
              onClick={() => handleToggleActive(record)}
              loading={updatingStatus}
            />
          </Tooltip>
          <Tooltip title={t("common.view")}>
            <Button
              className="btn-view"
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openViewPage(record.id)}
            />
          </Tooltip>
          <Tooltip title={t("common.edit")}>
            <Button
              className="btn-edit"
              type="primary"
              size="small"
              style={{ background: "#52c41a", borderColor: "#52c41a" }}
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Tooltip title={t("common.delete")}>
            <Button
              className="btn-delete"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => openDeleteModal(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title={t("themeManager.title")}
        extra={
          <Button type="primary" onClick={openCreateModal} disabled={loading}>
            {t("themeManager.createNew")}
          </Button>
        }
      >
        {/* Filters - Fixed */}
        <div
          style={{
            display: "flex",
            gap: "16px",
            marginBottom: 16,
            flexWrap: "wrap",
          }}
        >
          <Input
            placeholder={t("themeManager.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            style={{ width: 300 }}
            disabled={loading}
          />

          <Select
            placeholder={t("common.filterByStatus")}
            value={statusFilter || undefined}
            onChange={(value) => setStatusFilter(value || "")}
            options={STATUS_OPTIONS}
            style={{ width: 150 }}
            allowClear
            disabled={loading}
          />

          {hasFilters && (
            <Button onClick={resetFilters} disabled={loading}>
              {t("common.clearSearchToReorder")}
            </Button>
          )}
        </div>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={themeList}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50", "100"],
            showTotal: (total) =>
              `${t("common.showing")} ${total} ${t("themeManager.title")}`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size || 10);
            },
          }}
          locale={{
            emptyText: hasFilters
              ? t("themeManager.noMatchingThemes")
              : t("themeManager.noThemesFound"),
          }}
        />
      </Card>

      {/* Create/Edit Modal - Removed View Mode */}
      {isModalOpen && !isViewMode && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            resetForm();
          }}
          title={
            isEditMode
              ? t("themeManager.editTitle")
              : t("themeManager.createTitle")
          }
          onConfirm={isEditMode ? handleUpdate : handleCreate}
          confirmText={
            isEditMode
              ? updating || submitting
                ? t("themeManager.savingChanges")
                : t("common.update")
              : creating || submitting
                ? t("themeManager.creating")
                : t("common.create")
          }
          confirmDisabled={submitting}
          width={500}
        >
          <form onSubmit={isEditMode ? handleUpdate : handleCreate}>
            <div style={{ marginBottom: "15px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "500",
                }}
              >
                {t("common.name")} <span style={{ color: "red" }}>*</span>
              </label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder={t("themeManager.searchPlaceholder")}
                disabled={submitting}
                status={formErrors.name ? "error" : ""}
                maxLength={200}
              />
              <div
                style={{
                  fontSize: "12px",
                  color: "#666",
                  marginTop: "4px",
                  textAlign: "right",
                }}
              >
                {formData.name.length}/200 {t("common.characters")}
              </div>
              {formErrors.name && (
                <div
                  style={{
                    color: "#dc3545",
                    fontSize: "12px",
                    marginTop: "5px",
                  }}
                >
                  {formErrors.name}
                </div>
              )}
            </div>

            {/* Active Status */}
            <div style={{ marginBottom: "15px" }}>
              <label
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  disabled={submitting}
                />
                <span>{t("common.active")}</span>
              </label>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && themeToDelete && (
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setThemeToDelete(null);
          }}
          title={t("themeManager.confirmDelete")}
          onConfirm={handleDeleteConfirm}
          confirmText={deleting ? t("common.deleting") : t("common.delete")}
          confirmColor="danger"
          confirmDisabled={deleting}
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "40px", marginBottom: "10px" }}>⚠️</div>
            <h3 style={{ marginBottom: "15px" }}>
              {t("themeManager.confirmDelete")}
            </h3>

            <div
              style={{
                textAlign: "left",
                backgroundColor: "#f8f9fa",
                padding: "15px",
                borderRadius: "6px",
                marginBottom: "15px",
              }}
            >
              <p>
                <strong>{t("common.name")}:</strong> {themeToDelete.name}
              </p>
              <p>
                <strong>{t("common.createdAt")}:</strong>{" "}
                {formatDate(themeToDelete.created_at)}
              </p>
              <p>
                <strong>{t("common.status")}:</strong>
                <Tag
                  color={themeToDelete.is_active ? "green" : "default"}
                  style={{ marginLeft: "10px" }}
                >
                  {themeToDelete.is_active
                    ? t("common.active")
                    : t("common.inactive")}
                </Tag>
              </p>
            </div>

            <p style={{ color: "#dc3545" }}>
              {t("themeManager.deleteWarning")}
            </p>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ThemeManager;
